import { useEffect, useRef } from 'react'
import { WedgeDetector, type WedgeOptions } from '@/lib/barcode'

/**
 * Listens for a USB barcode gun ("keyboard wedge"): the gun types the code in
 * a burst and presses Enter. Fires `onScan(code)` for a burst, ignores people
 * typing (see WedgeDetector).
 *
 * The keystrokes still reach whatever input has focus — the browser cannot
 * know it is a scan until the Enter arrives. So the hook remembers what a
 * focused text box held when the burst began and puts it back once the scan
 * is recognised: a scan never leaves "8964000123456" in a quantity or lot
 * field. The closing Enter is swallowed, so a scan never submits the form it
 * happened to land in. A person typing is never touched (no burst, no undo).
 *
 * `enabled: false` detaches the listener (e.g. while a dialog owns the keys).
 */
export function useKeyboardWedgeScanner(
  onScan: (code: string) => void,
  { enabled = true, ...opts }: WedgeOptions & { enabled?: boolean } = {},
): void {
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan
  const { maxGapMs, minLength, enterGapMs } = opts

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const detector = new WedgeDetector({ maxGapMs, minLength, enterGapMs })
    let snapshot: { el: HTMLInputElement | HTMLTextAreaElement; value: string } | null = null
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        detector.reset()
        return
      }
      const code = detector.feed(e.key, e.timeStamp || performance.now())
      if (detector.startedBurst) {
        const el = e.target
        snapshot =
          el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ? { el, value: el.value } : null
      }
      if (code) {
        e.preventDefault()
        e.stopPropagation()
        if (snapshot && snapshot.el.isConnected && snapshot.el.value !== snapshot.value) {
          restoreInputValue(snapshot.el, snapshot.value)
        }
        snapshot = null
        onScanRef.current(code)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [enabled, maxGapMs, minLength, enterGapMs])
}

/**
 * Sets a React-controlled input's value and fires the input event React
 * listens for, so its state follows (assigning .value alone would be undone on
 * the next render).
 */
function restoreInputValue(el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}
